import { computed, onBeforeUnmount, ref } from 'vue';
import type { ChatMessage } from '../types/chat';

type StreamStatus = 'idle' | 'thinking' | 'streaming' | 'done' | 'error';

function safeRandomId(prefix: string): string {
  // Evita dependência extra de uuid; em browsers modernos, crypto.randomUUID existe.
  const anyCrypto = globalThis.crypto as Crypto | undefined;
  const uuid = anyCrypto?.randomUUID?.();
  return `${prefix}_${uuid || `${Date.now()}_${Math.random().toString(16).slice(2)}`}`;
}

function parseSseLines(buffer: string): { events: string[]; rest: string } {
  // SSE separa eventos por linha em branco.
  const parts = buffer.split(/\n\n/);
  const rest = parts.pop() ?? '';
  return { events: parts, rest };
}

function extractSseData(eventBlock: string): string[] {
  // Considera múltiplas linhas `data:` no mesmo evento.
  const lines = eventBlock.split('\n');
  const dataLines = lines
    .filter((l) => l.startsWith('data:'))
    .map((l) => l.replace(/^data:\s?/, ''));
  return dataLines;
}

export function useChatStreaming() {
  const messages = ref<ChatMessage[]>([]);
  const status = ref<StreamStatus>('idle');
  const errorMessage = ref<string | null>(null);

  const activeSessionId = ref<string | null>(null);
  const abortController = ref<AbortController | null>(null);

  const isThinking = computed(() => status.value === 'thinking');
  const isStreaming = computed(() => status.value === 'streaming' || status.value === 'thinking');

  function setSession(sessionId: string) {
    activeSessionId.value = sessionId;
  }

  function setMessages(next: ChatMessage[]) {
    messages.value = next;
  }

  function appendMessage(msg: ChatMessage) {
    messages.value = [...messages.value, msg];
  }

  function upsertAssistantDraft(sessionId: string, patch: string) {
    const idx = [...messages.value].reverse().findIndex((m) => m.sessionId === sessionId && m.role === 'assistant');
    const realIdx = idx === -1 ? -1 : messages.value.length - 1 - idx;
    if (realIdx === -1) {
      appendMessage({
        id: safeRandomId('assistant'),
        sessionId,
        role: 'assistant',
        content: patch,
        createdAt: new Date().toISOString(),
      });
      return;
    }

    const updated = [...messages.value];
    updated[realIdx] = { ...updated[realIdx], content: updated[realIdx].content + patch };
    messages.value = updated;
  }

  function stop() {
    abortController.value?.abort();
    abortController.value = null;
    status.value = 'idle';
  }

  async function streamFromResponse(response: Response, sessionId: string) {
    if (!response.body) {
      throw new Error('Resposta sem body para streaming.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let gotFirstToken = false;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Tenta SSE primeiro: eventos separados por linha em branco.
        const { events, rest } = parseSseLines(buffer);
        buffer = rest;

        if (events.length > 0) {
          for (const block of events) {
            const dataParts = extractSseData(block);
            for (const data of dataParts) {
              if (!data) continue;
              if (data === '[DONE]') {
                status.value = 'done';
                return;
              }

              // Se vier JSON, tentamos extrair { delta } / { content } / { text }.
              let token = data;
              if ((data.startsWith('{') && data.endsWith('}')) || (data.startsWith('[') && data.endsWith(']'))) {
                try {
                  const parsed = JSON.parse(data);
                  token =
                    parsed?.delta ??
                    parsed?.content ??
                    parsed?.text ??
                    parsed?.message ??
                    '';
                } catch {
                  // fica como texto
                }
              }

              if (!token) continue;
              if (!gotFirstToken) {
                gotFirstToken = true;
                status.value = 'streaming';
              }
              upsertAssistantDraft(sessionId, token);
            }
          }
          continue;
        }

        // Fallback: se o backend não envia SSE e sim texto “puro”
        if (!gotFirstToken && buffer.trim().length > 0) {
          gotFirstToken = true;
          status.value = 'streaming';
        }
        if (gotFirstToken && buffer.length > 0) {
          upsertAssistantDraft(sessionId, buffer);
          buffer = '';
        }
      }

      status.value = 'done';
    } finally {
      try {
        await reader.cancel();
      } catch {
        // ignore
      }
    }
  }

  async function sendUserMessage(opts: {
    sessionId: string;
    content: string;
    runRequest: (signal: AbortSignal) => Promise<Response>;
  }) {
    const { sessionId, content, runRequest } = opts;
    activeSessionId.value = sessionId;
    errorMessage.value = null;

    // Cancela stream anterior, se houver.
    abortController.value?.abort();
    abortController.value = new AbortController();

    appendMessage({
      id: safeRandomId('user'),
      sessionId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    });

    status.value = 'thinking';

    try {
      const response = await runRequest(abortController.value.signal);
      await streamFromResponse(response, sessionId);
    } catch (err: any) {
      status.value = 'error';
      errorMessage.value = err?.message || 'Erro ao gerar resposta.';
      throw err;
    } finally {
      abortController.value = null;
    }
  }

  onBeforeUnmount(() => stop());

  return {
    messages,
    status,
    errorMessage,
    activeSessionId,
    isThinking,
    isStreaming,
    setSession,
    setMessages,
    appendMessage,
    sendUserMessage,
    stop,
  };
}

